<template>
  <!-- Rendered into body so the modal never becomes part of the order details
       render tree - its mount, updates and unmount cannot interfere with the
       patching of that view. -->
  <Teleport to="body">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="inmodal">
          <div class="modal-header">
            <h4 class="modal-title">
              Prześlij plik podglądu do pozycji zamówienia
              {{ orderProduct?.pk }}
            </h4>
          </div>
          <div class="modal-body">
            <div class="form-horizontal">
              <div class="ibox-content">
                <div class="form-group">
                  <label class="control-label col-sm-2">Wybierz plik</label>
                  <div class="col-sm-10">
                    <div class="fileinput fileinput-new input-group">
                      <div class="form-control">
                        <input
                          ref="fileInput"
                          type="file"
                          name="file"
                          @change="onFileChange"
                        />
                      </div>
                    </div>
                    <div v-show="fileError" class="has-error">
                      <span class="help-block">{{ fileError }}</span>
                    </div>
                  </div>
                </div>

                <div v-show="productFile" class="form-group">
                  <label class="control-label col-sm-2">
                    Status weryfikacji pliku:
                  </label>
                  <div class="col-sm-10">
                    <span class="badge badge-secondary">
                      {{ productFile?.preflight_status?.message }}
                    </span>
                    <span
                      v-show="isPreflightInProgress"
                      class="fa fa-spinner fa-spin"
                    ></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button @click="close" class="btn btn-white">Zamknij</button>
            <button
              v-if="canAddOrderProductFile"
              :disabled="isPreflightInProgress || isUploading"
              @click="submitForm"
              class="btn btn-primary"
            >
              {{ isUploading ? "Przesyłanie..." : "Prześlij" }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div @click="close" class="modal-backdrop"></div>
  </Teleport>
</template>

<script>
import { computed, onUnmounted, ref } from "vue";
import { useStore } from "vuex";
import { hasPermission } from "../../permissions";
import { orderProductFilePermissionsConst } from "../../constants/orderConstants";

// Mirrors OrderProductFileModalController from the old admin panel. It lives
// in its own component so the preflight polling only re-renders the modal and
// never the whole order details view.
export default {
  name: "OrderProductFileModal",
  props: {
    orderProduct: { type: Object, required: true },
  },
  emits: ["close", "uploaded"],

  setup(props, { emit }) {
    const store = useStore();
    // Replacing a file deletes the previous one first, so the upload needs both
    // permissions.
    const canAddOrderProductFile = computed(
      () =>
        hasPermission(orderProductFilePermissionsConst.add) &&
        hasPermission(orderProductFilePermissionsConst.delete)
    );
    const fileInput = ref(null);
    const selectedFile = ref(null);
    const fileError = ref(null);
    const isUploading = ref(false);
    const isPreflightInProgress = ref(false);
    const productFile = ref(props.orderProduct.product_file || null);

    let preflightTimeout = null;

    const clearPreflightTimeout = () => {
      if (preflightTimeout) {
        clearTimeout(preflightTimeout);
        preflightTimeout = null;
      }
    };

    const close = () => {
      clearPreflightTimeout();
      emit("close");
    };

    const onFileChange = (event) => {
      const files = event.target.files || event.dataTransfer.files;
      fileError.value = null;
      selectedFile.value = files.length ? files[0] : null;
    };

    const getResponseErrorMessage = (error, fallback) => {
      const data = error?.response?.data;
      if (!data) {
        return fallback;
      }
      if (typeof data === "string") {
        return data;
      }
      const entries = Object.entries(data);
      if (!entries.length) {
        return fallback;
      }
      const [key, value] = entries[0];
      return `${key}: ${Array.isArray(value) ? value[0] : value}`;
    };

    // Polls the file until the verification is finished, the same way the old
    // admin panel does it.
    const awaitPreflightDone = () => {
      isPreflightInProgress.value =
        !!productFile.value &&
        productFile.value.preflight_status?.status_code === null;

      if (!isPreflightInProgress.value) {
        store.dispatch("showToaster", {
          type: "success",
          content: "Zakończono proces weryfikacji pliku",
        });

        const uploaded = {
          orderProductPk: props.orderProduct.pk,
          productFile: productFile.value,
        };
        preflightTimeout = setTimeout(() => {
          emit("uploaded", uploaded);
          emit("close");
        }, 500);
        return;
      }

      preflightTimeout = setTimeout(() => {
        store
          .dispatch("getOrderProductFileDetail", productFile.value.pk)
          .then((response) => {
            productFile.value = response.data;
            awaitPreflightDone();
          })
          .catch(() => {
            isPreflightInProgress.value = false;
            store.dispatch("showToaster", {
              type: "error",
              content: "Błąd podczas pobierania danych pliku",
            });
          });
      }, 3000);
    };

    const createFile = () => {
      return store
        .dispatch("createOrderProductFile", {
          orderProductPk: props.orderProduct.pk,
          file: selectedFile.value,
          isBookletUv: props.orderProduct.is_booklet_uv,
        })
        .then((response) => {
          productFile.value = response.data;
          selectedFile.value = null;
          if (fileInput.value) {
            fileInput.value.value = "";
          }
          store.dispatch("showToaster", {
            type: "success",
            content: "Dodano plik projektowy do pozycji zamówienia",
          });
          awaitPreflightDone();
        })
        .catch((error) => {
          fileError.value = getResponseErrorMessage(
            error,
            "Nie udało się dodać pliku projektowego."
          );
          store.dispatch("showToaster", {
            type: "error",
            content: fileError.value,
          });
        })
        .finally(() => {
          isUploading.value = false;
        });
    };

    const submitForm = () => {
      if (!canAddOrderProductFile.value) {
        return;
      }

      if (!selectedFile.value) {
        fileError.value = "Wybierz plik.";
        return;
      }

      fileError.value = null;
      isUploading.value = true;

      // An existing file is removed first so the new one is verified from
      // scratch - the same replace flow as in the old admin panel.
      if (productFile.value?.pk) {
        const previousPk = productFile.value.pk;
        productFile.value = null;
        store
          .dispatch("deleteOrderProductFile", previousPk)
          .then(createFile)
          .catch(createFile);
        return;
      }

      createFile();
    };

    onUnmounted(clearPreflightTimeout);

    return {
      fileInput,
      fileError,
      productFile,
      isUploading,
      isPreflightInProgress,
      onFileChange,
      submitForm,
      canAddOrderProductFile,
      close,
    };
  },
};
</script>
